import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TransactionCommissions = ({ transactions, filters }) => {
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedTransactions, setSelectedTransactions] = useState([]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success/10 text-success border-success/20';
      case 'pending':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'disputed':
        return 'bg-error/10 text-error border-error/20';
      default:
        return 'bg-muted/10 text-muted-foreground border-muted/20';
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSelectTransaction = (transactionId) => {
    setSelectedTransactions(prev => {
      if (prev?.includes(transactionId)) {
        return prev?.filter(id => id !== transactionId);
      } else {
        return [...prev, transactionId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedTransactions?.length === transactions?.length) {
      setSelectedTransactions([]);
    } else {
      setSelectedTransactions(transactions?.map(t => t?.id));
    }
  };

  const sortedTransactions = [...transactions]?.sort((a, b) => {
    let aVal = a?.[sortField];
    let bVal = b?.[sortField];
    
    if (sortField === 'date') {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    }
    
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  const getSortIcon = (field) => {
    if (sortField !== field) return 'ArrowUpDown';
    return sortDirection === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const totalCommissions = transactions?.reduce((sum, t) => sum + t?.commissionAmount, 0);
  const selectedCommissions = transactions
    ?.filter(t => selectedTransactions?.includes(t?.id))
    ?.reduce((sum, t) => sum + t?.commissionAmount, 0);

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Transaction Commission Details</h3>
          <p className="text-sm text-muted-foreground">
            {transactions?.length} transactions • Total: ${totalCommissions?.toFixed(2)}
          </p>
        </div>
        
        {selectedTransactions?.length > 0 && (
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">
              {selectedTransactions?.length} selected (${selectedCommissions?.toFixed(2)})
            </span>
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              iconPosition="left"
              iconSize={14}
            >
              Export
            </Button>
          </div>
        )}
      </div>
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-2">
                <input
                  type="checkbox"
                  checked={selectedTransactions?.length === transactions?.length}
                  onChange={handleSelectAll}
                  className="rounded border-border"
                />
              </th>
              <th className="text-left py-3 px-2">
                <button
                  onClick={() => handleSort('id')}
                  className="flex items-center space-x-1 text-muted-foreground hover:text-foreground"
                >
                  <span>Transaction ID</span>
                  <Icon name={getSortIcon('id')} size={14} />
                </button>
              </th>
              <th className="text-left py-3 px-2">
                <button
                  onClick={() => handleSort('date')}
                  className="flex items-center space-x-1 text-muted-foreground hover:text-foreground"
                >
                  <span>Date</span>
                  <Icon name={getSortIcon('date')} size={14} />
                </button>
              </th>
              <th className="text-left py-3 px-2">Customer</th>
              <th className="text-right py-3 px-2">
                <button
                  onClick={() => handleSort('transactionAmount')}
                  className="flex items-center space-x-1 text-muted-foreground hover:text-foreground"
                >
                  <span>Amount</span>
                  <Icon name={getSortIcon('transactionAmount')} size={14} />
                </button>
              </th>
              <th className="text-right py-3 px-2">Rate</th>
              <th className="text-right py-3 px-2">
                <button
                  onClick={() => handleSort('commissionAmount')}
                  className="flex items-center space-x-1 text-muted-foreground hover:text-foreground"
                >
                  <span>Commission</span>
                  <Icon name={getSortIcon('commissionAmount')} size={14} />
                </button>
              </th>
              <th className="text-left py-3 px-2">Status</th>
              <th className="text-left py-3 px-2">Territory</th>
            </tr>
          </thead>
          <tbody>
            {sortedTransactions?.map((transaction) => (
              <tr key={transaction?.id} className="border-b border-border/50 hover:bg-muted/30">
                <td className="py-3 px-2">
                  <input
                    type="checkbox"
                    checked={selectedTransactions?.includes(transaction?.id)}
                    onChange={() => handleSelectTransaction(transaction?.id)}
                    className="rounded border-border"
                  />
                </td>
                <td className="py-3 px-2">
                  <span className="font-mono text-xs text-primary">{transaction?.id}</span>
                </td>
                <td className="py-3 px-2 text-foreground">
                  {new Date(transaction?.date)?.toLocaleDateString()}
                </td>
                <td className="py-3 px-2 text-foreground">{transaction?.customerName}</td>
                <td className="py-3 px-2 text-right text-foreground">
                  ${transaction?.transactionAmount?.toLocaleString()}
                </td>
                <td className="py-3 px-2 text-right text-foreground">
                  {transaction?.commissionRate}%
                </td>
                <td className="py-3 px-2 text-right font-medium text-primary">
                  ${transaction?.commissionAmount?.toFixed(2)}
                </td>
                <td className="py-3 px-2">
                  <span className={`text-xs px-2 py-1 rounded-full border ${getStatusColor(transaction?.status)}`}>
                    {transaction?.status}
                  </span>
                </td>
                <td className="py-3 px-2 text-muted-foreground text-xs">
                  {transaction?.territory}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="md:hidden space-y-4">
        {sortedTransactions?.map((transaction) => (
          <div
            key={transaction?.id}
            className="bg-background border border-border rounded-lg p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={selectedTransactions?.includes(transaction?.id)}
                  onChange={() => handleSelectTransaction(transaction?.id)}
                  className="rounded border-border"
                />
                <span className="font-mono text-xs text-primary">{transaction?.id}</span>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full border ${getStatusColor(transaction?.status)}`}>
                {transaction?.status}
              </span>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Customer:</span>
                <span className="text-foreground">{transaction?.customerName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Date:</span>
                <span className="text-foreground">
                  {new Date(transaction?.date)?.toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Amount:</span>
                <span className="text-foreground">
                  ${transaction?.transactionAmount?.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Rate:</span>
                <span className="text-foreground">{transaction?.commissionRate}%</span>
              </div>
              <div className="flex justify-between font-medium">
                <span className="text-muted-foreground">Commission:</span>
                <span className="text-primary">${transaction?.commissionAmount?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Territory:</span>
                <span className="text-foreground text-xs">{transaction?.territory}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Summary Footer */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-4">
            <span className="text-muted-foreground">
              Showing {transactions?.length} transactions
            </span>
            {selectedTransactions?.length > 0 && (
              <span className="text-primary">
                {selectedTransactions?.length} selected
              </span>
            )}
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <span className="text-muted-foreground">Total Commission: </span>
              <span className="font-medium text-foreground">
                ${totalCommissions?.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionCommissions;